import { atom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, use, useCallback, useContext } from 'react';

export type AsideState = 'closed' | 'open' | 'unmounted';

const ASIDE_ANIMATION_TIME = 200;

export function createAsideAtoms() {
  const asideStateAtom = atom<AsideState>('unmounted');

  const openAsideAtom = atom(null, (get, set) => {
    const currentValue = get(asideStateAtom);
    if (currentValue !== 'open') {
      set(asideStateAtom, 'closed');
      setTimeout(() => {
        set(asideStateAtom, 'open');
      });
    }
  });

  const isAsideOpenAtom = atom((get) => {
    const asideState = get(asideStateAtom);
    return asideState === 'open';
  });

  const closeAsideAtom = atom(null, (get, set, onClose: () => void) => {
    const currentValue = get(asideStateAtom);
    set(asideStateAtom, 'closed');
    if (currentValue === 'closed') {
      set(asideStateAtom, 'unmounted');
      onClose();
    } else {
      window.setTimeout(() => {
        set(asideStateAtom, 'unmounted');
        onClose();
      }, ASIDE_ANIMATION_TIME);
    }
  });

  return {
    asideStateAtom,
    isAsideOpenAtom,
    openAsideAtom,
    closeAsideAtom,
  };
}

interface AsideContextValue {
  atoms: ReturnType<typeof createAsideAtoms>;
}

export const AsideContext = createContext<AsideContextValue>({
  atoms: {
    asideStateAtom: atom<AsideState>('unmounted'),
    isAsideOpenAtom: atom(false),
    openAsideAtom: atom(null, () => {
      throw new Error('Hooks to change aside state must be used inside <AsideProvider>');
    }),
    closeAsideAtom: atom(null, () => {
      throw new Error('Hooks to change aside state must be used inside <AsideProvider>');
    }),
  },
});

export const useAsideState = (): AsideState => {
  const { atoms } = useContext(AsideContext);
  return useAtomValue(atoms.asideStateAtom);
};

export const useIsAsideOpen = (): boolean => {
  const asideContext = use(AsideContext);
  return useAtomValue(asideContext.atoms.isAsideOpenAtom);
};

export const useCloseAside = () => {
  const { atoms } = useContext(AsideContext);
  const closeAside = useSetAtom(atoms.closeAsideAtom);
  return useCallback(
    (onClose: () => void) => {
      closeAside(onClose);
    },
    [closeAside],
  );
};
